import {gql} from '@apollo/client';
import produce from 'immer';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {FirstLookupStepFormProps} from 'lookup-config/first-lookup-step/FirstLookupStepFormSection';
import LookupConfigModal, {
  LookupConfigModalProps,
  MultiLookupConfigSubmitItem,
} from 'lookup-config/LookupConfigModal';
import {
  LookupStepSpec,
  LookupConfigSpec,
  LookupStepType,
  NOTEBOOK_FINAL_LOOKUP_STEP_TYPES,
  NOTEBOOK_INTERMEDIATE_LOOKUP_STEP_TYPES,
  NotebookColumnItemType,
  LookupSortType,
} from 'lookup-config/LookupConfigModalConstants';
import * as LookupColumnsDAO from 'notebook/structured-tables/lookup-columns/LookupColumnsDAO';
import {
  DedicatedLookupStructuredTableColumn,
  LookupConfig,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
} from 'notebook/tables/constants/TableColumnConstants';
import notifications from 'notifications';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';

import {getColumnLabel} from '../columns/ColumnDefinitions';
import toColumnDefinition from '../columns/toColumnDefinition';
import {makeStructuredTableColumn} from '../util/StructuredTableColumnUtil';
import {NotebookImplementedInterfacesQuery} from './__generated/NotebookImplementedInterfacesQuery';
import {NotebookLookupConfigModalQuery} from './__generated/NotebookLookupConfigModalQuery';
import NotebookFirstLookupStepForm from './NotebookFirstLookupStepForm';
import {
  getDefaultLookupStepForInputColumn,
  getInitialInputColumnProvenanceUuid,
} from './NotebookLookupColumnUtils';

const CREATE_LUC_MODAL_SUBMIT_BUTTON_TEXT = 'Insert';
const EDIT_LUC_MODAL_SUBMIT_BUTTON_TEXT = 'Update';

export interface NotebookColumnInfo {
  name: string;
  provenanceUuid: string;
  itemType: NotebookColumnItemType;
  // Additional metadata to be smarter about what following lookups to show when we don't know what specific
  // type a storable lookup is, but know it must be a parent storable
  storableLookupMustBeParent?: boolean;
  schemaId?: string | null;
  schemaInterfaceId?: string | null;
}

export function createLookupColumnFromConfig({
  lookupConfig,
  finalStepName,
  notebookInputColumnProvenanceUuid,
  unitApiIdentifier,
  lookupSortType,
}: {
  lookupConfig: LookupConfig;
  finalStepName: string;
  notebookInputColumnProvenanceUuid?: string | null;
  unitApiIdentifier?: string | null;
  lookupSortType?: LookupSortType;
}): InsertableColumnInfo {
  assert(
    notebookInputColumnProvenanceUuid != null,
    'Lookup column was configured without any column to use as input'
  );

  const structuredTableColumn: DedicatedLookupStructuredTableColumn = {
    ...makeStructuredTableColumn({
      columnType: TableColumnType.DEDICATED_LOOKUP,
      name: finalStepName,
    }),
    columnType: TableColumnType.DEDICATED_LOOKUP, // needed to satisfy TypeScript
    frontEndMetadata: undefined,
    lookupConfig,
    inputColumnProvenanceUuid: notebookInputColumnProvenanceUuid,
    unitApiIdentifier,
    lookupSortType,
  };
  const columnDefinition = toColumnDefinition({structuredTableColumn, schemas: []});
  return {
    columnLabels: getColumnLabel(columnDefinition),
    columnConstraints: {
      structuredTableColumn,
      columnDefinition,
    },
    widths: structuredTableColumn.width,
  };
}

async function _createLookupColumnInfoFromSubmitItem(
  item: MultiLookupConfigSubmitItem<ExtraState>
): Promise<InsertableColumnInfo> {
  const {
    lookupConfig: lookupConfigSpec,
    finalStepName,
    finalStepSchemaField,
    extraState,
    finalSortType,
  } = item;
  const {lookupConfig} = await withLoadingScreen(
    LookupColumnsDAO.createNotebookLookupConfig(lookupConfigSpec)
  );
  return createLookupColumnFromConfig({
    lookupConfig,
    finalStepName,
    notebookInputColumnProvenanceUuid: extraState.notebookInputColumnProvenanceUuid,
    unitApiIdentifier: finalStepSchemaField?.unitApiIdentifier,
    lookupSortType: finalSortType,
  });
}

interface ExtraState {
  notebookInputColumnProvenanceUuid: string | null;
}

export function renderFirstLookupStepFormForNotebook(
  firstLookupStepFormProps: FirstLookupStepFormProps<ExtraState>,
  notebookColumnInfos: Array<NotebookColumnInfo>,
  implementingSchemaIdsByInterfaceId: Map<string, Set<string>> = new Map()
): JSX.Element {
  return (
    <NotebookFirstLookupStepForm
      disabled={firstLookupStepFormProps.disabled}
      notebookColumnInfos={notebookColumnInfos}
      notebookInputColumnProvenanceUuid={
        firstLookupStepFormProps.extraState.notebookInputColumnProvenanceUuid
      }
      setNotebookInputColumnProvenanceUuid={(provenanceUuid: string | null) => {
        firstLookupStepFormProps.setExtraState({notebookInputColumnProvenanceUuid: provenanceUuid});
      }}
      lookupStepGroup={firstLookupStepFormProps.lookupStepGroup}
      updateLookupStepGroup={firstLookupStepFormProps.handleSetLookupStepGroup}
      implementingSchemaIdsByInterfaceId={implementingSchemaIdsByInterfaceId}
    />
  );
}

export function getInitialLookupConfigForCreateModal(
  notebookColumnInfos: Array<NotebookColumnInfo>
): LookupConfigSpec | null {
  const initialInputColumnProvenanceUuid = getInitialInputColumnProvenanceUuid(notebookColumnInfos);
  const {lookupStep: initialLookupStep} = getDefaultLookupStepForInputColumn(
    notebookColumnInfos,
    initialInputColumnProvenanceUuid
  );
  return initialLookupStep
    ? {
        isMulti: true,
        lookupSteps: [initialLookupStep],
      }
    : null;
}

export const NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY = gql`
  query NotebookLookupConfigModalQuery @skipAudit @engComponent(name: NOTEBOOK) {
    ...AllAssaySchemasGraphQL
    ...AllStorableSchemasGraphQL
    ...AllCompleteTagSchemasGraphQL
    ...AllSchemaInterfacesGraphQL
  }
  ${getFragmentWrapper('AllAssaySchemas').fragment}
  ${getFragmentWrapper('AllStorableSchemas').fragment}
  ${getFragmentWrapper('AllCompleteTagSchemas').fragment}
  ${getFragmentWrapper('AllSchemaInterfaces').fragment}
`;

export const NOTEBOOK_IMPLEMENTED_INTERFACES_QUERY = gql`
  query NotebookImplementedInterfacesQuery @skipAudit @engComponent(name: NOTEBOOK) {
    tagSchemas {
      id
      interfaces {
        id
      }
    }
  }
`;

async function fetchModalDataAndImplementingSchemaIds(
  notebookColumnInfos: Array<NotebookColumnInfo>
): Promise<Map<string, Set<string>>> {
  const hasFieldsetColumns = notebookColumnInfos.some((c) => c.schemaInterfaceId != null);

  const [, interfacesResult] = await withLoadingScreen(
    Promise.all([
      doQuerySafe<NotebookLookupConfigModalQuery, {}>({query: NOTEBOOK_LOOKUP_CONFIG_MODAL_QUERY}),
      hasFieldsetColumns
        ? doQuerySafe<NotebookImplementedInterfacesQuery, {}>({
            query: NOTEBOOK_IMPLEMENTED_INTERFACES_QUERY,
          }).catch(() => null)
        : Promise.resolve(null),
    ])
  );

  const implementingSchemaIdsByInterfaceId = new Map<string, Set<string>>();
  if (interfacesResult?.tagSchemas != null) {
    for (const schema of interfacesResult.tagSchemas) {
      for (const iface of schema.interfaces) {
        let schemaIds = implementingSchemaIdsByInterfaceId.get(iface.id);
        if (schemaIds == null) {
          schemaIds = new Set();
          implementingSchemaIdsByInterfaceId.set(iface.id, schemaIds);
        }
        schemaIds.add(schema.id);
      }
    }
  }
  return implementingSchemaIdsByInterfaceId;
}

/**
 * Parse the table and run the LookupConfigModal, and return whatever lookup columns the user configures.
 *
 * To be used when inserting new lookup columns to a structured table. Multiple columns may be
 * returned when the user selects more than one attribute in the detail pane.
 */
export async function runCreateLookupColumnConfigModal(
  notebookColumnInfos: Array<NotebookColumnInfo>
): Promise<{
  lookupColumnsToInsert: Array<InsertableColumnInfo>;
} | null> {
  if (notebookColumnInfos.length === 0) {
    // This red toast may be jarring, but it is better than the alternatives:
    //
    // - Showing the config modal with an error message, which would require more time and more clicks from the user
    // - Disabling the "Insert lookup column" menu options without explanation, which would be very confusing
    // - Disabling the "Insert lookup column" menu options and changing the option names include an explanation,
    //   because the explanation would need to be ridiculously long
    //
    // Ideally we could disable the menu options and show a tooltip, but that's hard to do with our current context menu implementation
    notifications.error(
      'This table does not support lookup columns. ' +
        'To support lookup columns, a table must have at least one column that contains either entities or inventory items.',
      {
        duration: 10,
      }
    );
    return null;
  }
  // Show loading screen and fetch everything
  // This might take a little time the first time the modal is opened, but if the modal is opened again all the data should be in cache
  const implementingSchemaIdsByInterfaceId =
    await fetchModalDataAndImplementingSchemaIds(notebookColumnInfos);
  const initialLookupConfig = getInitialLookupConfigForCreateModal(notebookColumnInfos);

  const multipleResults = await legacyRunModal<LookupConfigModalProps<ExtraState>>(LookupConfigModal, {
    renderFirstLookupStepForm: (props: FirstLookupStepFormProps<ExtraState>) =>
      renderFirstLookupStepFormForNotebook(props, notebookColumnInfos, implementingSchemaIdsByInterfaceId),
    initialExtraState: {
      notebookInputColumnProvenanceUuid: getInitialInputColumnProvenanceUuid(notebookColumnInfos),
    },
    baseSchema: null,
    initialLookupConfig,
    firstLookupStepTypes: [LookupStepType.NOTEBOOK_COLUMN_TYPECHECK],
    intermediateLookupStepTypes: [
      ...NOTEBOOK_INTERMEDIATE_LOOKUP_STEP_TYPES,
      LookupStepType.FILTER_BY_ORIGIN,
    ],
    finalLookupStepTypes: NOTEBOOK_FINAL_LOOKUP_STEP_TYPES,
    submitButtonText: CREATE_LUC_MODAL_SUBMIT_BUTTON_TEXT,
    allowMissingParentSchema: true,
    allowMultipleDetailSelections: true,
    allowInventoryFilters: true,
  });
  if (multipleResults == null) {
    return null;
  }

  const lookupColumnsToInsert = await withLoadingScreen(
    Promise.all(
      multipleResults.map(async ({lookupConfig: lookupConfigSpec, extraState, ...item}) => {
        const {lookupConfig} = await LookupColumnsDAO.createNotebookLookupConfig(lookupConfigSpec);
        const lookupStepsTypes = lookupConfigSpec.lookupSteps.map((lookupStep) => lookupStep.type);
        const firstLookupStep = lookupConfigSpec.lookupSteps[0];
        assert(
          firstLookupStep.type === LookupStepType.NOTEBOOK_COLUMN_TYPECHECK,
          'First lookup step should be notebook column typecheck.'
        );
        analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.CREATE_NOTEBOOK_LOOKUP_CONFIG, {
          lookupConfigId: lookupConfig.id,
          lookupStepsTypes, // Deprecated - add fields to LookupStepAnalyticsMetadata instead of adding more arrays here.
          lookupStepsMetadata: lookupConfigSpec.lookupSteps.map((lss) => getLookupStepAnalyticsMetadata(lss)),
          lookupInputColumnType: firstLookupStep.notebookColumnItemType,
          lookupInputColumnEntitySchema: firstLookupStep.notebookColumnEntitySchema,
          lookupInputColumnStorableSchema: firstLookupStep.notebookColumnStorableSchema,
        });
        return createLookupColumnFromConfig({
          lookupConfig,
          ...item,
          lookupSortType: item.finalSortType,
          unitApiIdentifier: item.finalStepSchemaField?.unitApiIdentifier,
          notebookInputColumnProvenanceUuid: extraState.notebookInputColumnProvenanceUuid,
        });
      })
    )
  );

  return {lookupColumnsToInsert};
}

/**
 * Analytics metadata for a single lookup step.
 *
 * Avoid any breaking changes, as the data team's SQL queries rely on this shape.
 */
interface LookupStepAnalyticsMetadata {
  lookupStepType: LookupStepType;

  // Defined for SCHEMA_FIELD lookup steps.
  schemaField?: string;
}

/**
 * Get a metadata object for the given lookup step for analytics purposes.
 */
function getLookupStepAnalyticsMetadata(lookupStepSpec: LookupStepSpec): LookupStepAnalyticsMetadata {
  const metadata: LookupStepAnalyticsMetadata = {lookupStepType: lookupStepSpec.type};

  if (lookupStepSpec.type === LookupStepType.SCHEMA_FIELD) {
    // The schema field can be null if the field has been deleted, but we don't expect that here, since we
    // created the step right before capturing this metadata.
    metadata.schemaField = lookupStepSpec.schemaField!;
  }

  return metadata;
}

export async function runEditLookupConfigModal(
  notebookColumnInfos: Array<NotebookColumnInfo>,
  originalLookupColumnConstraint: ColumnConstraint,
  initialInputColumnInfo?: NotebookColumnInfo,
  initialLookupConfig?: LookupConfigSpec
): Promise<InsertableColumnInfo | null> {
  // Show loading screen and fetch everything
  // This might take a little time the first time the modal is opened, but if the modal is opened again all the data should be in cache
  const implementingSchemaIdsByInterfaceId =
    await fetchModalDataAndImplementingSchemaIds(notebookColumnInfos);
  const selectedLookupIsParent = initialInputColumnInfo?.storableLookupMustBeParent;
  const columnDefinition = originalLookupColumnConstraint.columnDefinition;
  assert(
    columnDefinition != null && columnDefinition.type === TableColumnType.DEDICATED_LOOKUP,
    'Column to edit is not a lookup column.'
  );

  const submitResult = await legacyRunModal<LookupConfigModalProps<ExtraState>>(LookupConfigModal, {
    renderFirstLookupStepForm: (props: FirstLookupStepFormProps<ExtraState>) =>
      renderFirstLookupStepFormForNotebook(props, notebookColumnInfos, implementingSchemaIdsByInterfaceId),
    initialExtraState: {
      notebookInputColumnProvenanceUuid: getInitialInputColumnProvenanceUuid(
        notebookColumnInfos,
        initialInputColumnInfo
      ),
    },
    baseSchema: null,
    initialLookupConfig,
    firstLookupStepTypes: [LookupStepType.NOTEBOOK_COLUMN_TYPECHECK],
    intermediateLookupStepTypes: [
      ...NOTEBOOK_INTERMEDIATE_LOOKUP_STEP_TYPES,
      LookupStepType.FILTER_BY_ORIGIN,
    ],
    finalLookupStepTypes: NOTEBOOK_FINAL_LOOKUP_STEP_TYPES,
    submitButtonText: EDIT_LUC_MODAL_SUBMIT_BUTTON_TEXT,
    allowMissingParentSchema: true,
    allowInventoryFilters: true,
    context: {
      storableLookupMustBeParent: selectedLookupIsParent,
    },
    finalSortType: columnDefinition.lookupSortType,
  });

  // Create new insertable column info based on selections in LookupConfigModal
  // Note that we will always create a new lookup config even if the user submits an identical config to the
  // one they were initially editing.
  assert(
    submitResult == null || submitResult.length <= 1,
    'Expected one item from edit modal, but received multiple.'
  );
  const singleItem = submitResult?.[0] ?? null;
  const brandNewLookupColumnInfo =
    singleItem != null ? await _createLookupColumnInfoFromSubmitItem(singleItem) : null;
  if (brandNewLookupColumnInfo == null) {
    return null;
  }

  // Modify the "new" insertable info to keep the same provenance UUID and cid as the existing lookup column.
  const updatedOriginalLookupColumnInfo = produce(brandNewLookupColumnInfo, (draftLookupColumnInfo) => {
    const {structuredTableColumn} = draftLookupColumnInfo.columnConstraints!;
    assert(
      structuredTableColumn != null,
      'InsertableColumnInfo was created from lookup config without a column constraint / structured table column.'
    );
    structuredTableColumn.cid = originalLookupColumnConstraint.structuredTableColumn!.cid;
    structuredTableColumn.provenanceUuid =
      originalLookupColumnConstraint.structuredTableColumn!.provenanceUuid;
    return draftLookupColumnInfo;
  });
  return updatedOriginalLookupColumnInfo;
}
